import 'babel-polyfill';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  ContentState,
  convertFromHTML,
  CompositeDecorator,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState
} from 'draft-js';
import { OrderedSet } from 'immutable';
import classNames from 'classnames';
import { stateToMarkdown } from '../../../libraries/draft-js-export-markdown';
import { stateFromMarkdown } from '../../../libraries/draft-js-import-markdown';
import { Image, Link } from './entities/Entities';
import { StyleControls } from './style-controls/StyleControls';
import {
  CharactersPrompt,
  ImagePrompt,
  LinkPrompt,
  SupSubPrompt
} from './prompts/Prompts';
import InputError from '../../../errors/InputError';
import './RichTextEditor.css';

export default class RichTextEditor extends Component {
  constructor(props) {
    super(props);

    const decorator = new CompositeDecorator([
      {
        strategy: findLinkEntities,
        component: Link
      },
      {
        strategy: findImageEntities,
        component: Image
      }
    ]);

    this.state = {
      charactersPromptShowing: false,
      contentInMarkdown: (this.props.existingContent) ? this.props.existingContent : "",
      decorator: decorator,
      editorState: EditorState.createWithContent(stateFromMarkdown((this.props.existingContent) ? this.props.existingContent : ""), decorator),
      isContentSet: false,
      imagePromptShowing: false,
      linkPromptShowing: false,
      promptForText: false,
      supSubPromptShowing: false
    };
  }

  componentWillReceiveProps(newProps) {
    if((newProps.existingContent &&
         newProps.existingContent !== this.state.contentInMarkdown &&
         !this.state.isContentSet
        ) ||
        (this.props.isEditable &&
         !newProps.isEditable
        )
      ) {

      this.setState(() => {

        let newEditorState = EditorState.createWithContent(stateFromMarkdown(newProps.existingContent), this.state.decorator);

        let styledEditorState = this.addLinkStyles(newEditorState);

        // Closes all open panels when the editor is going into read-only mode
        let closePanels = (this.props.isEditable && !newProps.isEditable) && {
          charactersPromptShowing: false,
          imagePromptShowing: false,
          linkPromptShowing: false,
          supSubPromptShowing: false
        };
        return {
          contentInMarkdown: newProps.existingContent,
          editorState: styledEditorState,
          isContentSet: true,
          ...closePanels
        };
      });
    }

    // Remove this when extra blank lines are cleared out of data
    // Only removing blank blocks when the user goes into edit mode
    if(!this.props.isEditable && newProps.isEditable) {
      let noBlanksEditorState = removeBlankBlocks(this.state.editorState);

      this.handleChange(noBlanksEditorState);
    }
  }

  getValue = () => {
    const currentContent = this.state.editorState.getCurrentContent();
    const plainText = currentContent.getPlainText().trim();

    // Case to handle if there is no actual text in the section but styles are applying, with exception for just image(s) content
    if (plainText === "" && !this.state.contentInMarkdown.startsWith("![")) {
      if (this.props.required) {
        throw new InputError(this.props.label + " is Required", "");
      }
      return "";
    }

    return this.state.contentInMarkdown;
  }

  // Updates editorState and markdown when content is changed
  handleChange = (editorState, isHandlingBlur=false) => {
    this.setState(() => {
      let contentInMarkdown = stateToMarkdown(editorState.getCurrentContent()).trim();

      if (isHandlingBlur) {
        this.handleBlur(contentInMarkdown);
      }

      return { contentInMarkdown, editorState };
    });
  }

  // Passes the editor's markdown content and id to function received from props
  handleBlur = (newMarkdownContent) => {
    let markdownContent = (typeof newMarkdownContent === "string") ?
      newMarkdownContent :
      this.state.contentInMarkdown;

    if (this.props.onBlur) {
      this.props.onBlur(markdownContent, this.props.id);
    }
  }

  handlePastedText = (pastedText, pastedHTML) => {

    /*if(document.documentMode || /Edge/.test(navigator.userAgent)) {
    }
    else {*/

      // Standard copy paste procedures
      const { editorState } = this.state;
      let newContentState, pastedBlockMap = null;
      const textLengthBeforeCursor = editorState.getSelection().getFocusOffset();

      if (pastedHTML) {
        const blocksFromHTML = convertFromHTML(pastedHTML.split("</html>")[0] + "</html>");

        pastedBlockMap = ContentState.createFromBlockArray(blocksFromHTML).blockMap;
      }
      else {
        pastedBlockMap = ContentState.createFromText(pastedText).blockMap;
      }

      newContentState = Modifier.replaceWithFragment(
        editorState.getCurrentContent(), editorState.getSelection(),
        pastedBlockMap
      );
    
      let insertedEditorState = EditorState.push(
        editorState,
        newContentState,
        'insert-fragment'
      );
    
      const currentCursorKey = insertedEditorState.getCurrentContent().selectionBefore.focusKey;

      // Adds the link styles into the pasted content
      const linkEditorState = this.addLinkStyles(insertedEditorState);
      let cursorEditorState = linkEditorState;
      const pastedBlockMapArray = pastedBlockMap.toArray();
      const pastedBlockMapLength = pastedBlockMapArray.length;

      // Handles cursor placement after content has been pasted
      if (pastedBlockMapLength === 1) {
        cursorEditorState = cursorToBlockPosition(
          linkEditorState,
          currentCursorKey,
          textLengthBeforeCursor + pastedBlockMapArray[0].text.length
        );
      }
      else if (pastedBlockMapLength > 1) {
        const contentBlockArray = linkEditorState.getCurrentContent().getBlocksAsArray();

        const newCursorKeyIndex = contentBlockArray
          .findIndex((block) => block.key === currentCursorKey) + pastedBlockMapArray.length - 1;

        const newCursorKey = contentBlockArray[newCursorKeyIndex].key;

        const lastPastedBlock = pastedBlockMapArray[pastedBlockMapArray.length - 1];

        cursorEditorState = cursorToBlockPosition(
          linkEditorState,
          newCursorKey,
          lastPastedBlock.text.length
        );
      }

      // Sets new editor state and updates markdown
      this.handleChange(cursorEditorState, true);

      // Prevents built-in copy-paste behaviors
      return true;
    //}
  }

  // When enter key is pressed
  handleReturn = (event) => {
    if (event.shiftKey) {
      this.handleChange(RichUtils.insertSoftNewline(this.state.editorState));
      return 'handled';
    }
  }

  // For nesting lists
  onTab = (event) => {
    this.handleChange(RichUtils.onTab(event, this.state.editorState, 6));
  }

  // Updates block types of selected content
  toggleBlockType = (blockType) => {
    this.handleChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType
      )
    );
  }

  // Updates inline styles of selected content
  toggleInlineStyle = (inlineStyle) => {
    this.handleChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        inlineStyle
      )
    );
  }

  // Shows or hides the link content prompt
  toggleLinkPrompt = (promptForText) => {
    this.setState((prevState) => {

      let linkPromptShowing = !prevState.linkPromptShowing;

      let closeOthers = {};

      if (linkPromptShowing) {
        closeOthers.imagePromptShowing = false;
        closeOthers.charactersPromptShowing = false;
        closeOthers.supSubPromptShowing = false;
      }

      return { promptForText, linkPromptShowing, ...closeOthers };
    });
  }

  // Decides what content to prompt for in the link prompt
  promptForLink = (linkStyle) => {
    const selection = this.state.editorState.getSelection();

    if (!selection.isCollapsed()) {
      this.toggleLinkPrompt();
    }
    else {
      this.toggleLinkPrompt(true);
    }

    // TO-DO??: This really belongs in confirmLink, but for some reason it breaks when I try to add it in there.
    this.handleChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        linkStyle
      )
    );
  }

  // Handled the link content returned from the prompt and applies the entities and styles to the content
  confirmLink = (linkUrl, linkText) => {
    this.toggleLinkPrompt();

    const selection = this.state.editorState.getSelection();
    const contentState = this.state.editorState.getCurrentContent();

    // Text highlighted case
    if(!selection.isCollapsed() && linkUrl) {
      const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url: linkUrl });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      this.handleChange(RichUtils.toggleLink(this.state.editorState, selection, entityKey), true);
    }
    // Text added case
    else if (selection.isCollapsed() && linkUrl && linkText) {
      const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url: linkUrl });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const style = this.state.editorState.getCurrentInlineStyle();

      const newEditorState = insertText(this.state.editorState, linkText, OrderedSet.of(...style, 'link'), entityKey);
      const spaceEditorState = insertText(newEditorState, " ");

      this.handleChange(spaceEditorState, true);
    }
  }

  // Removes the link entity and style of the selected content
  removeLink = (toggleStyle) => {
    const state = this.state.editorState;
    const selection = state.getSelection();

    if (!selection.isCollapsed()) {

      let newEditorState = removeEntity(state);

      this.handleChange(RichUtils.toggleInlineStyle(
        newEditorState,
        toggleStyle
      ));
    }
    else {
      /* TO-DO: Bugfix: make entity stop being contiguous for next characters

      This will turn the style off, but the entity still exists so it's still in the link in the markdown. vvvv

      this.handleChange(RichUtils.toggleInlineStyle(
        state,
        toggleStyle
      ));

      */
    }
  }

  // Closes Link Prompt and reverts focus to the text editor
  cancelLink = () => {
    this.toggleLinkPrompt();
    this.editorRef.focus();
  }

  addLinkStyles = (editorState) => {
    let contentState = editorState.getCurrentContent();
    let linkBlocks = [];

    contentState.getBlocksAsArray().forEach((block) => {
      findLinkEntities(block, (start, end) => {
        linkBlocks.push({ block, start, end });
      }, contentState)
    });

    let newEditorState = (linkBlocks) ?
      addStylesToBlockRanges(editorState, linkBlocks, "link") :
      editorState;

    const noSelectionEditorState = EditorState.moveSelectionToEnd(
      newEditorState
    );

    return noSelectionEditorState;
  }

  // Shows or hides the image content prompt
  toggleImagePrompt = () => {
    this.setState((prevState) => {

      let imagePromptShowing = !prevState.imagePromptShowing;

      let closeOthers = {};

      if (imagePromptShowing) {
        closeOthers.linkPromptShowing = false;
        closeOthers.charactersPromptShowing = false;
        closeOthers.supSubPromptShowing = false;
      }

      return { imagePromptShowing, ...closeOthers };
    });
  }

  // Decides what content to prompt for in the image prompt
  promptForImage = () => {
    const selection = this.state.editorState.getSelection();

    if (selection.isCollapsed()) {
      this.toggleImagePrompt();
    }
  }

  // Takes the image content provided and inserts the image where the cursor is in the editor
  confirmImage = (imageUrl, imageAltText) => {
    this.toggleImagePrompt();

    if (imageUrl) {
      const initialEditorState = this.state.editorState;
      const initialContentState = initialEditorState.getCurrentContent();
      const initialSelection = initialEditorState.getSelection();
      const initialSelectionOffset = initialSelection.getStartOffset();
      const initialSelectionBlockKey = initialSelection.getStartKey();

      // Selection offset will be 0 if at the beginning
      const isAtBeginningOfBlock = !initialSelectionOffset;
      // Selection offset will match block's length if at end
      const isAtEndOfBlock = (initialSelectionOffset === initialContentState.getBlockForKey(initialSelectionBlockKey).getLength());
      const isBlankBlock = (isAtBeginningOfBlock && isAtEndOfBlock);
      const hasFollowingBlock = initialContentState.getBlockAfter(initialSelectionBlockKey);

      let finalEditorState = initialEditorState;

      // Case 1: Inserted into a blank block
      if (isBlankBlock) {
        const imageEditorState = addImageEntity(initialEditorState, imageUrl, imageAltText);

        if (hasFollowingBlock) {
          finalEditorState = imageEditorState;
        }
        else {
          const trailingBlockObject = insertBreak(imageEditorState, "split");

          finalEditorState = cursorToBlockPosition(trailingBlockObject.editorState, trailingBlockObject.blockKey, 0);
        }
      }
      // Case 2: Inserted at beginning of block
      else if (isAtBeginningOfBlock) {
        const precedingBlockObject = insertBreak(initialEditorState, "split");

        const newEditorState = cursorToBlockPosition(precedingBlockObject.editorState, precedingBlockObject.editorState.getCurrentContent().getKeyBefore(precedingBlockObject.blockKey), 0);

        finalEditorState = addImageEntity(newEditorState, imageUrl, imageAltText);
      }
      // Case 3: Inserted at end of block
      else if (isAtEndOfBlock) {
        const precedingBlockObject = insertBreak(initialEditorState, "split");

        const imageEditorState = addImageEntity(precedingBlockObject.editorState, imageUrl, imageAltText);

        if (hasFollowingBlock) {
          finalEditorState = imageEditorState;
        }
        else {
          const trailingBlockObject = insertBreak(imageEditorState, "split");

          finalEditorState = cursorToBlockPosition(trailingBlockObject.editorState, trailingBlockObject.blockKey, 0);
        }
      }
      // Case 4: Inserted in middle of block
      else {
        const precedingBlockObject = insertBreak(initialEditorState, "block");

        const newEditorState = cursorToBlockPosition(precedingBlockObject.editorState, precedingBlockObject.blockKey, 0);

        finalEditorState = addImageEntity(newEditorState, imageUrl, imageAltText);
      }

      this.handleChange(finalEditorState, true);
    }
  }

  // Closes Image Prompt and reverts focus to the text editor
  cancelImage = () => {
    this.toggleImagePrompt();
    this.editorRef.focus();
  }

  // Shows or hides the characters content prompt
  toggleCharactersPrompt = () => {
    this.setState((prevState) => {

      let charactersPromptShowing = !prevState.charactersPromptShowing;

      let closeOthers = {};

      if (charactersPromptShowing) {
        closeOthers.linkPromptShowing = false;
        closeOthers.imagePromptShowing = false;
        closeOthers.supSubPromptShowing = false;
      }

      return { charactersPromptShowing, ...closeOthers };
    });
  }

  insertCharacters = (characters) => {
    const selection = this.state.editorState.getSelection();
    const contentState = this.state.editorState.getCurrentContent();

    const newContentState = Modifier.replaceText(contentState, selection, characters);

    const newEditorState = EditorState.push(
      this.state.editorState,
      newContentState,
      'insert-characters'
    );

    const finalEditorState = cursorToBlockPosition(
      newEditorState,
      selection.getStartKey(),
      selection.getStartOffset() + characters.length
    );

    this.handleChange(finalEditorState, true);
  }

  confirmCharacters = (character) => {
    this.toggleCharactersPrompt();
    this.insertCharacters(character);
  }

  // Closes Characters Prompt and reverts focus to the text editor
  cancelCharacters = () => {
    this.toggleCharactersPrompt();
    this.editorRef.focus();
  }

  // Shows or hides the superscript/subscript content prompt
  toggleSupSubPrompt = () => {
    this.setState((prevState) => {

      let supSubPromptShowing = !prevState.supSubPromptShowing;

      let closeOthers = {};

      if (supSubPromptShowing) {
        closeOthers.linkPromptShowing = false;
        closeOthers.imagePromptShowing = false;
        closeOthers.charactersPromptShowing = false;
      }

      return { supSubPromptShowing, ...closeOthers };
    });
  }

  confirmSupSub = (characters) => {
    this.toggleSupSubPrompt();
    this.insertCharacters(characters);
  }

  // Closes Superscript/Subscript Prompt and reverts focus to the text editor
  cancelSupSub = () => {
    this.toggleSupSubPrompt();
    this.editorRef.focus();
  }

  render() {
    let readOnlyToggleClass = classNames('field' , 'ccf-rich-text-editor', {
      'read-only': !this.props.isEditable
    });

    let requiredFieldIndicator = (this.props.required && this.props.isEditable) ?
      <abbr title="This field is required">*</abbr> :
      null;

    const label = (this.props.label) ?
      <label>
        {this.props.label}
      </label> :
      null;

    let errorMessage = (this.props.errorMessage) ?
      <span className="error-message">
        {this.props.errorMessage}
      </span> :
      null;

    return (
      <div className={readOnlyToggleClass} id={this.props.id}>
        {requiredFieldIndicator}{label}
        <StyleControls
          editorState={this.state.editorState}
          onBlockToggle={this.toggleBlockType}
          onInlineToggle={this.toggleInlineStyle}
          textDirection={this.props.textDirection}
          blockOptions={[
            {label: 'H1', style: 'header-one'},
            {label: 'H2', style: 'header-two'},
            {label: 'H3', style: 'header-three'},
            {label: 'H4', style: 'header-four'},
            {label: 'H5', style: 'header-five'},
            {label: 'H6', style: 'header-six'},
            {label: 'Quote', style: 'blockquote'},
            {label: 'Bullets', style: 'unordered-list-item'},
            {label: 'Numbers', style: 'ordered-list-item'},
          ]}
          inlineOptions={[
            {label: 'Bold', style: 'BOLD'},
            {label: 'Italic', style: 'ITALIC'}
          ]}
          toggleOptions={[
            {
              activeLabel: 'Unlink',
              label: 'Link',
              onActiveToggle: this.removeLink,
              onInactiveToggle: this.promptForLink,
              style: 'link'
            },
            {
              label: 'Image',
              onInactiveToggle: this.promptForImage
            },
            {
              label: 'Characters',
              onInactiveToggle: this.toggleCharactersPrompt
            },
            {
              label: 'Sup/Sub',
              onInactiveToggle: this.toggleSupSubPrompt
            },
          ]}
        />
        <LinkPrompt
          handleCancel={this.cancelLink}
          handleSubmit={this.confirmLink}
          formId={this.props.id + "-link-prompt"}
          isShowing={this.state.linkPromptShowing}
          promptForText={this.state.promptForText}
        />
        <ImagePrompt
          handleCancel={this.cancelImage}
          handleSubmit={this.confirmImage}
          formId={this.props.id + "-image-prompt"}
          isShowing={this.state.imagePromptShowing}
        />
        <CharactersPrompt
          formId={this.props.id + "-characters-prompt"}
          handleCancel={this.cancelCharacters}
          handleSubmit={this.confirmCharacters}
          isShowing={this.state.charactersPromptShowing}
        />
        <SupSubPrompt
          formId={this.props.id + "-sup-sub-prompt"}
          handleCancel={this.cancelSupSub}
          handleSubmit={this.confirmSupSub}
          isShowing={this.state.supSubPromptShowing}
        />
        <Editor
          editorState={this.state.editorState}
          handlePastedText={this.handlePastedText}
          handleReturn={this.handleReturn}
          readOnly={!this.props.isEditable}
          ref={instance => { this.editorRef = instance; }}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          onTab={this.onTab}
          textDirectionality={this.props.textDirection}
        />
        {errorMessage}
      </div>
    );
  }
}

RichTextEditor.defaultProps = {
  isEditable: true,
  textDirection: 'LTR'
}

RichTextEditor.propTypes = {
  errorMessage: PropTypes.string,
  existingContent: PropTypes.string,
  id: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  textDirection: PropTypes.oneOf(['RTL', 'LTR'])
}

// Identifies where link entities exist within the content
const findLinkEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();

      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    callback
  );
}

// Identifies where image entities exist within the content
function findImageEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'IMAGE'
      );
    },
    callback
  );
}

// Inserts either a split or full new block at the current cursor position
const insertBreak = (editorState, splitOrBlock) => {
  const contentState = editorState.getCurrentContent();

  const splitContentState = Modifier.splitBlock(contentState, editorState.getSelection());
  const splitContentStateFinal = (splitOrBlock === "block") ?
    Modifier.splitBlock(splitContentState, editorState.getSelection()) :
    splitContentState;

  let newBlockKey = splitContentStateFinal.getBlockAfter(splitContentStateFinal.selectionBefore.focusKey).key;

  return ({
    blockKey: newBlockKey,
    editorState: EditorState.push(
      editorState,
      splitContentStateFinal,
      "insert-fragment"
    )
  })
}

// Takes an editorState and the key of a block, and puts the cursor at the position specified in the block
export const cursorToBlockPosition = (editorState, blockKey, newPosition) => {
  const selectionState = SelectionState.createEmpty();

  const blockSelectionState = selectionState.merge({
    anchorKey: blockKey,
    anchorOffset: newPosition,
    focusKey: blockKey,
    focusOffset: newPosition
  });

  return (
    EditorState.forceSelection(
      editorState,
      blockSelectionState
    )
  );
}

// Adds new text with the provided information and editorState and returns an updated editorState
const insertText = (editorState, text, style, entityKey) => {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    const newContentState = Modifier.insertText(contentState, selection, text, style, entityKey);
    const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');

    return newEditorState;
  }

// Removes the entity from the given editorState's selected content and returns the updated editorState
const removeEntity = (editorState) => {
  let newContentState = Modifier.applyEntity(editorState.getCurrentContent(), editorState.getSelection(), null);
  let newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');

  return newEditorState;
}

// Adds styles to the ranges provided within blocks.
// Block objects should have the format { block (object), start (integer), end (integer) }
const addStylesToBlockRanges = (editorState, blockObjects, inlineStyle) => {
  let newEditorState = editorState;

  if (blockObjects && inlineStyle) {
    blockObjects.forEach((blockObject) => {
      if( !(blockObject.block.getInlineStyleAt(blockObject.start).toArray().includes(inlineStyle))) {
        let oldEditorState = newEditorState;

        const selectionState = SelectionState.createEmpty();
        const blockSelectionState = selectionState.merge({
          anchorKey: blockObject.block.key,
          anchorOffset: blockObject.start,
          focusKey: blockObject.block.key,
          focusOffset: blockObject.end,
          hasFocus: false
        });

        newEditorState = RichUtils.toggleInlineStyle(EditorState.forceSelection(
            oldEditorState,
            blockSelectionState
          ),
          inlineStyle
        );
      }
    });
  }

  return newEditorState;
}

// Remove this when extra blank lines are cleared out of data - unless a reason to keep this is seen.
// Makes a new EditorState with any empty blocks removed
const removeBlankBlocks = (editorState) => {
  let contentState = editorState.getCurrentContent();

  // Editor will always have 1 block even if empty
  if (contentState.getBlocksAsArray().length > 1) {
    let newContentBlockArray = [];

    contentState.getBlocksAsArray().forEach((block) => {
        if(block.text.length) {
          newContentBlockArray.push(block);
        }
    });

    // New editor must still have at least 1 block
    if (newContentBlockArray.length) {
      let newContentState = ContentState.createFromBlockArray(newContentBlockArray);

      let newEditorState = EditorState.push(editorState, newContentState, 'remove-range');

      return newEditorState;
    }
  }

  return editorState;
}

// Takes the editor state and the image information and inserts the image's entity
const addImageEntity = (editorState, imageUrl, imageAltText) => {
  const contentStateWithEntity = editorState.getCurrentContent().createEntity('IMAGE', 'IMMUTABLE', { src: imageUrl, alt: imageAltText });

  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  return insertText(editorState, ' ', null, entityKey);
}



// WEBPACK FOOTER //
// ./src/shared/components/presentation/rich-text-editor/RichTextEditor.js